import { api } from "@/lib/api/client";
import { unwrap } from "@/lib/api/unwrap";
import { applyAccountLanguage } from "@/lib/auth/account-language";
import { fetchMe } from "@/lib/auth/auth-api";
import { setTokens } from "@/lib/auth/tokens";

/**
 * The public enrolment endpoints (OAC-464 part 2).
 *
 * The first two are reachable without a token; the third needs the session the
 * second one opens.
 *
 * `submitEnrolment` reports whether a link was sent. Both outcomes are HTTP
 * 200 — the status code stays uniform — but the body distinguishes them,
 * because AC 7 asks for "le même message de refus" and a participant who
 * mistypes their code has to be told. What stays indistinguishable is the
 * *cause*: an unknown code, a closed group and a retired code all come back
 * with the identical sentence.
 *
 * `throttled` is the one thing the body adds. It is not about the code — the
 * budgets are spent before the code is resolved — so it tells the participant
 * to wait instead of sending them to check a code that may be fine.
 */
export async function submitEnrolment(payload: {
  code: string;
  email: string;
}): Promise<{ sent: boolean; throttled: boolean }> {
  const result = (await unwrap(
    api.POST("/api/v1/enrolment/", {
      body: { code: payload.code, email: payload.email },
    } as never),
  )) as { sent: boolean; throttled: boolean; detail: string };

  return { sent: result.sent, throttled: result.throttled };
}

export interface EnrolmentSession {
  access: string;
  refresh: string;
  /** Null on an ordinary verification; set when another group was submitted. */
  transfer_to: string | null;
  transfer_to_name: string | null;
  transfer_from_name: string | null;
  /** The signed permit that must come back with the answer. */
  transfer_ticket: string | null;
}

/**
 * Consume a verification link. Stores the token pair, so the participant is
 * signed in from here on — including while they answer the transfer offer.
 *
 * The link works once: the backend moves `last_login` when it mints the pair,
 * which is what stops the token in the link verifying a second time.
 */
export async function verifyEnrolmentLink(
  payload: string,
): Promise<EnrolmentSession> {
  const session = (await unwrap(
    api.POST("/api/v1/enrolment/verify/", { body: { payload } } as never),
  )) as EnrolmentSession;

  setTokens({ access: session.access, refresh: session.refresh });
  // As login() does: switch to the account's language, or record the one in
  // use. Not awaited — the link has been spent and the participant is in.
  void fetchMe()
    .then(applyAccountLanguage)
    .catch(() => {});
  return session;
}

/**
 * Answer the transfer offer. `confirm: false` is a no-op server-side; the
 * ticket is required either way because it is what names the target group —
 * the SPA has no server-side session to hold it in, and a caller-supplied
 * group id would let any participant join any cohort without its code.
 */
export async function answerTransfer(payload: {
  confirm: boolean;
  ticket: string;
}): Promise<void> {
  await unwrap(
    api.POST("/api/v1/enrolment/transfer/", { body: payload } as never),
  );
}
